import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { PrivateWaypoint, WaypointCategory } from '../types';
import { PrivateWaypointsService } from '../services/privateWaypoints';

interface PrivateWaypointsState {
  waypoints: PrivateWaypoint[];
  loading: boolean;
  error: string | null;
  showPrivateWaypoints: boolean;
  selectedCategories: WaypointCategory[];
  hasMore: boolean;
  currentOffset: number;
  totalCount: number;
}

const initialState: PrivateWaypointsState = {
  waypoints: [],
  loading: false,
  error: null,
  showPrivateWaypoints: true,
  selectedCategories: [],
  hasMore: true,
  currentOffset: 0,
  totalCount: 0,
};

interface FetchWaypointsParams {
  reset?: boolean;
  limit?: number;
  searchTerm?: string;
  category?: WaypointCategory | 'all';
}

export const fetchPrivateWaypoints = createAsyncThunk(
  'privateWaypoints/fetch',
  async (params: FetchWaypointsParams = {}): Promise<{
    waypoints: PrivateWaypoint[];
    total: number;
    hasMore: boolean;
    reset: boolean;
  }> => {
    const { reset = false, limit = 50, searchTerm = '', category = 'all' } = params;
    
    const result = await PrivateWaypointsService.searchWaypoints({
      searchTerm,
      category,
      limit,
      offset: reset ? 0 : undefined
    });
    
    return {
      waypoints: result.waypoints,
      total: result.total,
      hasMore: result.hasMore,
      reset
    };
  }
);

export const createPrivateWaypoint = createAsyncThunk(
  'privateWaypoints/create',
  async (waypoint: Omit<PrivateWaypoint, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<PrivateWaypoint> => {
    return await PrivateWaypointsService.createWaypoint(waypoint);
  }
);

export const updatePrivateWaypoint = createAsyncThunk(
  'privateWaypoints/update',
  async ({ id, updates }: { 
    id: string; 
    updates: Partial<Omit<PrivateWaypoint, 'id' | 'userId' | 'createdAt'>> 
  }): Promise<{ id: string; updates: Partial<Omit<PrivateWaypoint, 'id' | 'userId' | 'createdAt'>> }> => {
    await PrivateWaypointsService.updateWaypoint(id, updates);
    return { id, updates };
  }
);

export const deletePrivateWaypoint = createAsyncThunk(
  'privateWaypoints/delete',
  async (id: string): Promise<string> => {
    await PrivateWaypointsService.deleteWaypoint(id);
    return id;
  }
);

const privateWaypointsSlice = createSlice({
  name: 'privateWaypoints',
  initialState,
  reducers: {
    togglePrivateWaypoints: (state) => {
      state.showPrivateWaypoints = !state.showPrivateWaypoints;
    },
    setShowPrivateWaypoints: (state, action: PayloadAction<boolean>) => {
      state.showPrivateWaypoints = action.payload;
    },
    toggleCategory: (state, action: PayloadAction<WaypointCategory>) => {
      const category = action.payload;
      const index = state.selectedCategories.indexOf(category);
      if (index >= 0) {
        state.selectedCategories.splice(index, 1);
      } else {
        state.selectedCategories.push(category);
      }
    },
    setSelectedCategories: (state, action: PayloadAction<WaypointCategory[]>) => {
      state.selectedCategories = action.payload;
    },
    clearSelectedCategories: (state) => {
      state.selectedCategories = [];
    },
    resetPagination: (state) => {
      state.currentOffset = 0;
      state.hasMore = true;
      state.waypoints = [];
    },
  },
  extraReducers: (builder) => {
    // Fetch waypoints
    builder.addCase(fetchPrivateWaypoints.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPrivateWaypoints.fulfilled, (state, action) => {
      state.loading = false;
      
      if (action.payload.reset) {
        state.waypoints = action.payload.waypoints;
        state.currentOffset = action.payload.waypoints.length;
      } else {
        state.waypoints = [...state.waypoints, ...action.payload.waypoints];
        state.currentOffset += action.payload.waypoints.length;
      }
      
      state.hasMore = action.payload.hasMore;
      state.totalCount = action.payload.total;
    });
    builder.addCase(fetchPrivateWaypoints.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message ?? 'Failed to fetch waypoints';
    });

    // Create waypoint
    builder.addCase(createPrivateWaypoint.fulfilled, (state, action) => {
      state.waypoints.unshift(action.payload);
    });

    // Update waypoint
    builder.addCase(updatePrivateWaypoint.fulfilled, (state, action) => {
      const index = state.waypoints.findIndex(wp => wp.id === action.payload.id);
      if (index >= 0) {
        state.waypoints[index] = {
          ...state.waypoints[index],
          ...action.payload.updates,
          updatedAt: new Date().toISOString(),
        };
      }
    });

    // Delete waypoint
    builder.addCase(deletePrivateWaypoint.fulfilled, (state, action) => {
      state.waypoints = state.waypoints.filter(wp => wp.id !== action.payload);
    });
  },
});

export const {
  togglePrivateWaypoints,
  setShowPrivateWaypoints,
  toggleCategory,
  setSelectedCategories,
  clearSelectedCategories,
  resetPagination,
} = privateWaypointsSlice.actions;

export default privateWaypointsSlice.reducer;